import {
  useEffect,
  useContext,
  useRef,
  useState,
} from 'react';
import { useSelector } from 'react-redux';
import loadable from '@utils/loadable';
import classNames from 'classnames/bind';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAngleUp } from '@fortawesome/pro-regular-svg-icons/faAngleUp';
import { faAngleDown } from '@fortawesome/pro-regular-svg-icons/faAngleDown';

import { newsTabs, dataTabs, newsAndContentBySourceTabs } from '../../../data/directory/constants';

import withMetatag from '../../../decorators/metadataDecorators/metatagsTitleDecorator';
import withComponentName from '../../../decorators/withComponentName';

import useSizes from '../../../hooks/sizes';

import PiwikContext from '../../../services/piwikContext';

import Styles from './styles.module.scss';

const cx = classNames.bind(Styles);

const DirectoryTab = loadable(() => import('./DirectoryTab'));

const MainDirectory = () => {
  const searchBarActive = useSelector(({ searchReducers }) => searchReducers.searchBarActive);
  const blurSearchbar = useSelector(({ searchReducers }) => searchReducers.blur);
  const permissions = useSelector(({ subscriptions }) => subscriptions.permissions);
  const { piwikEnabled } = useContext(PiwikContext);
  useEffect(() => {
    if (piwikEnabled) {
      _paq.push(['trackPageView']);
    }
  }, [piwikEnabled]);

  return (
    <div className={blurSearchbar && searchBarActive ? Styles.mainDirectory_blured : ''}>
      <article className={Styles.directory}>
        <section>
          <h1 className={Styles.title}>Directory</h1>
          <DescriptionSection>
            We’ve made it easy to drill down into the financial market news universe.
            Simply choose a topic, asset class, or type of data to begin your exploratory
            journey and start reaping the benefits of CityFALCON’s proprietary machine
            learning algorithms to guide your way.
          </DescriptionSection>
        </section>
        <section>
          <h2 className={Styles.sub_header}>News and Content by Category</h2>
          <DescriptionSection>
            Delve into our databases of financial news and content, personalised to your needs.
            We’ve attached analytics to everything, connected the relevant traditional and
            alternative data, and built a browseable Big Data universe of all the topics and
            entities in our systems.
          </DescriptionSection>
          <div className={Styles.tabs}>
            {newsTabs.map((tab) => (
              <DirectoryTab key={tab.id} tab={tab} />
            ))}
          </div>
        </section>
        <section>
          <h3 className={Styles.sub_header}>News and Content by Source</h3>
          <DescriptionSection>
            Browse the latest content from the sources that most closely reflect your
            worldview, market outlook, and coverage style from local finance websites to
            the biggest news providers covering capital markets.
          </DescriptionSection>
          <div className={Styles.tabs}>
            {newsAndContentBySourceTabs.map((tab) => (
              <DirectoryTab key={tab.id} tab={tab} />
            ))}
          </div>
        </section>
        <section>
          <h3 className={Styles.sub_header}>Insights and Structured Data</h3>
          <DescriptionSection>
            The financial markets are messy enough as it is. Our algorithms scrape and process
            and structure financial data to streamline and simplify due diligence and research.
            Browse that neatly arranged data here.
          </DescriptionSection>
          <div className={Styles.tabs}>
            {dataTabs.map((tab) => (
              <DirectoryTab key={tab.id} tab={tab} permissions={permissions} />
            ))}
          </div>
        </section>
      </article>
    </div>
  );
};

const DescriptionSection = ({ children }) => {
  const descRef = useRef();
  const [hasShowMore, setHasShowMore] = useState(false);
  const [isShowMore, setIsShowMore] = useState(false);

  const { width } = useSizes();
  useEffect(() => {
    if (width < 700 && descRef.current) {
      return setHasShowMore(descRef.current.scrollHeight > 60);
    }
    setHasShowMore(false);
  }, [width]);

  const toggleExpand = () => {
    setIsShowMore(!isShowMore);
  };

  return (
    <>
      <p className={cx('main_desc', { expand: isShowMore })} ref={descRef}>
        {children}
      </p>
      {hasShowMore && (
        <div className={Styles.description_show} onClick={toggleExpand}>
          Show&nbsp;
          {isShowMore ? 'Less' : 'More'}
          <FontAwesomeIcon className="img mob" icon={isShowMore ? faAngleUp : faAngleDown} />
        </div>
      )}
    </>
  );
};

export default withComponentName(withMetatag(MainDirectory, 'news/directory'));
